import { createContext, useContext } from 'react';

/**
 * Context + imperative handle for the styled confirm dialog. The
 * `<ConfirmProvider>` (confirm-dialog.tsx) supplies the value and registers the
 * imperative handle; components call `useConfirm()` and non-component code calls
 * `confirmImperative(...)`. Split from the provider component so the module only
 * exports hooks/functions (Vite fast-refresh requires component-only files).
 */

export interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
  /**
   * Collects free text before confirming — e.g. a reason for voiding a receipt. `confirm()`
   * still only ever resolves yes/no, so the caller reads the typed value off `onChange` itself
   * rather than off the resolved promise.
   */
  input?: {
    label: string;
    placeholder?: string;
    required?: boolean;
    onChange: (value: string) => void;
  };
}

export type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

export const ConfirmContext = createContext<ConfirmFn | null>(null);

// Module-level handle so non-hook code (e.g. confirmDiscardIfDirty) can reach
// the mounted dialog without threading a hook through every caller.
let imperativeConfirm: ConfirmFn | null = null;

/** Called by ConfirmProvider on mount/unmount. */
export function registerImperativeConfirm(fn: ConfirmFn | null): void {
  imperativeConfirm = fn;
}

/** True only while a provider is mounted; used by the imperative fallback. */
export function hasImperativeConfirm(fn: ConfirmFn): boolean {
  return imperativeConfirm === fn;
}

/** Hook form of confirm — use inside React components. */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a <ConfirmProvider>');
  return ctx;
}

/**
 * Imperative confirm for non-component code (utility functions, route guards).
 * Routes through the mounted provider; falls back to the native confirm if (and
 * only if) none is mounted.
 */
export function confirmImperative(options: ConfirmOptions): Promise<boolean> {
  if (imperativeConfirm) return imperativeConfirm(options);
  return Promise.resolve(window.confirm(options.description ?? options.title));
}
